import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { VesTransactionsService } from '../../../services/ves-transactions.service';
import Swal from 'sweetalert2';

interface CandidatoN58 {
  id: number;
  referenciaBanco: string;
  monto: number;
  fecha: string;
  descripcion: string;
  concepto: string;
  cuenta: string;
  contraparte: string | null;
  montoCoincide: boolean;
}

/**
 * Emparejado manual de una referencia VES con un movimiento N58.
 *
 * Existe porque el match automatico por referencia falla seguido: el cliente
 * declara la referencia de SU banco (11 o 14 digitos) y N58 registra otra
 * distinta para la misma transferencia (7 u 8 digitos). Nunca cruzan.
 *
 * Hasta ahora el operador lo resolvia rechazando la referencia y cargando el
 * saldo a mano. Eso acreditaba al cliente pero dejaba el movimiento del banco
 * sin conciliar para siempre, y ademas quemaba la referencia. Acá el operador
 * elige el movimiento y el backend hace las dos cosas en una sola transaccion.
 *
 * Sólo N58. Banco Plaza y R4 no tienen este boton y su flujo no cambia.
 */
@Component({
  selector: 'app-emparejar-n58-modal',
  templateUrl: './emparejar-n58-modal.component.html',
})
export class EmparejarN58ModalComponent implements OnInit {
  referencia: any;

  cargando = true;
  guardando = false;

  emparejables: CandidatoN58[] = [];
  cercanos: CandidatoN58[] = [];
  seleccionado: number | null = null;

  constructor(
    private vesTransactionsService: VesTransactionsService,
    public dialogRef: MatDialogRef<EmparejarN58ModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.referencia = data;
  }

  ngOnInit(): void {
    this.cargarCandidatos();
  }

  cargarCandidatos(): void {
    this.cargando = true;
    this.seleccionado = null;

    this.vesTransactionsService.getCandidatosN58(this.referencia.id).subscribe({
      next: (resp) => {
        this.emparejables = resp?.data?.emparejables || [];
        this.cercanos = resp?.data?.cercanos || [];
        this.cargando = false;

        // Un solo candidato exacto es el caso normal: se preselecciona para que
        // el operador confirme en vez de tener que buscar.
        if (this.emparejables.length === 1) {
          this.seleccionado = this.emparejables[0].id;
        }
      },
      error: (err) => {
        this.cargando = false;
        Swal.fire({
          icon: 'error',
          title: 'No se pudieron cargar los movimientos',
          text:
            err?.error?.message ||
            err?.message ||
            'Error consultando los movimientos de N58',
          confirmButtonColor: '#1e5a3f',
        });
        this.dialogRef.close(false);
      },
    });
  }

  seleccionar(id: number): void {
    this.seleccionado = this.seleccionado === id ? null : id;
  }

  formatVES(amount: number | null | undefined): string {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'VES',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  confirmar(): void {
    if (this.guardando || this.seleccionado === null) return;

    const elegido = this.emparejables.find((c) => c.id === this.seleccionado);
    if (!elegido) return;

    Swal.fire({
      icon: 'question',
      title: '¿Emparejar esta referencia?',
      html:
        `<p style="margin-bottom:10px;">Se le acredita al cliente y se concilia el movimiento del banco.</p>` +
        `<div style="text-align:left;font-size:0.85rem;line-height:1.6;">` +
        `<div><strong>Referencia del cliente:</strong> ${this.referencia.referencia}</div>` +
        `<div><strong>Referencia del banco:</strong> ${elegido.referenciaBanco}</div>` +
        `<div><strong>Monto:</strong> ${this.formatVES(elegido.monto)}</div>` +
        `</div>`,
      showCancelButton: true,
      confirmButtonText: 'Sí, emparejar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1e5a3f',
    }).then((res) => {
      if (!res.isConfirmed) return;
      this.ejecutar(elegido);
    });
  }

  private ejecutar(elegido: CandidatoN58): void {
    this.guardando = true;

    this.vesTransactionsService
      .conciliarN58Manual(this.referencia.id, elegido.id)
      .subscribe({
        next: () => {
          this.guardando = false;
          Swal.fire({
            icon: 'success',
            title: 'Referencia emparejada',
            html:
              `<p>Saldo acreditado al cliente y movimiento del banco conciliado.</p>` +
              `<p style="margin-top:8px;font-size:0.85rem;color:#6b7280;">` +
              `Movimiento #${elegido.id} · Ref. banco ${elegido.referenciaBanco}</p>`,
            confirmButtonColor: '#1e5a3f',
          });
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.guardando = false;
          // 409: otro operador lo tomó entre la carga y el click. Vale la pena
          // recargar la lista en vez de dejar al operador con datos viejos.
          if (err?.status === 409) {
            Swal.fire({
              icon: 'warning',
              title: 'Ese movimiento ya no está disponible',
              text:
                err?.error?.message ||
                'Otro operador lo emparejó recién. Se recarga la lista.',
              confirmButtonColor: '#1e5a3f',
            }).then(() => this.cargarCandidatos());
            return;
          }

          Swal.fire({
            icon: 'error',
            title: 'No se pudo emparejar',
            text:
              err?.error?.message ||
              err?.message ||
              'Error al emparejar la referencia',
            confirmButtonColor: '#1e5a3f',
          });
        },
      });
  }

  cerrar(): void {
    this.dialogRef.close(false);
  }
}
